import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { useAcesso } from 'dashboard/composables/useAcesso';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

/**
 * FLOW-13 (SPEC S1 e Q5; PLAN G1): quem vê e mexe nos segredos da conta.
 *
 * O super-admin sempre. Na conta com a flag PowerAgents, o administrador e quem tem
 * `poweragents_manage` num papel personalizado. É a régua do item PowerAgents do menu
 * (`Sidebar.vue`), a mesma do servidor (`PowerAgentsGuard#gestor_com_flag?`), e **não** depende da
 * chave do PowerFlow (`POWERFLOW_ADMIN_MONTA_FLOW`): por isso não é o `useMontaFlow`, que depende dela
 * e olha só o administrador. O atendente fica de fora, e a API responde 403 a ele.
 *
 * Quem decide de verdade é o servidor; isto é a porta da frente: a sub-aba "Segredos" das
 * Ferramentas do agente, o item do menu e a aba Segredos de Configurações › PowerTools.
 *
 * - `gestorDoCofre`: vê a sub-aba e mexe nos segredos (criar, trocar valor, excluir).
 *
 * ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, o gestor é quem tem o `vault:use`, o escopo de ver os nomes do
 * cofre (`flow_secrets#index`), que é o que a sub-aba e os nomes no editor leem; gravar, trocar e apagar pedem o
 * `vault:manage`, que o servidor confere em cada um. Desligada, o papel, como acima. A flag continua por cima.
 */
export function useGestorDoCofre() {
  const { isAdmin, isSuperAdmin } = useAdmin();
  // `can` é a faixa do papel personalizado: para o administrador ela é falsa de propósito, e por
  // isso soma com o `isAdmin`, como no menu.
  const { can } = usePermissions();
  const { decide } = useAcesso();
  const accountId = useMapGetter('getCurrentAccountId');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const contaComFlag = computed(
    () =>
      !!isFeatureEnabledonAccount.value?.(
        accountId.value,
        FEATURE_FLAGS.POWER_AGENTS
      )
  );
  const gestorDoCofre = computed(
    () =>
      isSuperAdmin.value ||
      (contaComFlag.value &&
        decide('vault:use', () => isAdmin.value || can('poweragents_manage')))
  );

  return { gestorDoCofre };
}
